import { useReactiveVar } from '@apollo/client'
import { isOmWorkspaceUser } from 'api/graphQL/reactiveVars/workspaceContent'
import { usePreviewFeatures } from './usePreviewFeatures'

/**
 * Custom hook to determine if the user is currently viewing the OM (Office Master) Workspace.
 *
 * The OM Workspace has special behavior:
 * - Allows editing of inactive content
 * - Hides track changes styling
 * - Shows visibility toggles for content activation/deactivation
 *
 * @returns {Object} OM workspace state
 * @returns {boolean} isCurrentlyOnOMWorkspace - True if feature flag is enabled AND user is an OM user
 * @returns {boolean} isOMUser - True if the current user has OM workspace access
 * @returns {boolean} hasOMWorkspaceFeature - True if the OM workspace feature flag is enabled
 *
 * @example
 * ```tsx
 * const { isCurrentlyOnOMWorkspace, isOMUser } = useOMWorkspace()
 *
 *
 * // Allow editing inactive content in OM workspace
 * const isEditMode = shouldEdit && (isCurrentlyOnOMWorkspace ? true : !inactive)
 * ```
 */
export const useOMWorkspace = () => {
    const { previewFeatures } = usePreviewFeatures()
    const isOMUser = useReactiveVar(isOmWorkspaceUser)

    /**
     * Determines if the user is currently viewing the OM Workspace.
     * Both the feature flag AND user permission must be true.
     */
    const isCurrentlyOnOMWorkspace = Boolean(
        previewFeatures?.hasOMWorkspace && isOMUser
    )

    return {
        /**
         * True if both OM workspace feature flag is enabled AND user has OM access.
         * Use this for OM-specific behavior like hiding track changes or allowing inactive content editing.
         */
        isCurrentlyOnOMWorkspace,

        /**
         * True if the current user has OM workspace access (regardless of feature flag state).
         */
        isOMUser,

        /**
         * True if the OM workspace feature flag is enabled (regardless of user permissions).
         */
        hasOMWorkspaceFeature: Boolean(previewFeatures?.hasOMWorkspace),
    }
}
